import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SheetContent, SheetOverlay, SheetTitle } from '../tailgrids/core/sheet.js';
import { cn } from '../../utils/cn.js';
import { useUIStore } from '../../store/index.js';
import { PageTitleSlotContext } from './page-title-slot.js';
import { ShellHeader } from './ShellHeader.js';
import { ShellSidebar } from './ShellSidebar.js';

/**
 * The detailed app's frame (NextAdmin layout): a sidebar that collapses to an icon rail on wide
 * screens and slides in as a sheet below `xl`, and a framed main column with the header on top.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // A new page starts at the top.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <aside
        style={{
          width: sidebarOpen ? 270 : 72,
          minWidth: sidebarOpen ? 270 : 72,
          transition: 'width 300ms cubic-bezier(0.4,0,0.2,1), min-width 300ms cubic-bezier(0.4,0,0.2,1)',
        }}
        className="hidden shrink-0 overflow-hidden xl:block"
      >
        <ShellSidebar expanded={sidebarOpen} onToggle={toggleSidebar} />
      </aside>

      <SheetOverlay isOpen={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          showCloseButton={false}
          className="w-67.5! max-w-67.5! border-r border-card-border bg-card-surface-area p-0"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <ShellSidebar
            expanded
            isMobileSheet
            onToggle={() => setMobileNavOpen(false)}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </SheetContent>
      </SheetOverlay>

      <div className={cn('min-w-0 flex-1', sidebarOpen ? 'lg:p-4 xl:pl-0' : 'lg:p-4')}>
        <div className="flex h-full flex-col overflow-hidden border-[0.5px] border-card-surface-border bg-card-surface-area lg:rounded-2xl lg:shadow-[0_3px_6px_-2px_rgba(0,0,0,0.02),0_1px_1px_0_rgba(0,0,0,0.04)]">
          <PageTitleSlotContext value={titleSlot}>
            <ShellHeader onOpenNavigation={() => setMobileNavOpen(true)} titleSlotRef={setTitleSlot} />
            <main ref={mainRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
              {/* --page-height: the viewport left inside the page padding (header 72.5px + paddings), so full-height
                  templates (WorkspacePage, ChatPage) never hard-code a calc of the shell chrome. */}
              <div className="mx-auto w-full max-w-384 p-4 text-heading [--page-height:calc(100dvh-105px)] lg:p-6 lg:[--page-height:calc(100dvh-154px)]">
                {children}
              </div>
            </main>
          </PageTitleSlotContext>
        </div>
      </div>
    </div>
  );
}
